import { NAVIGATION_FROM_URL_PARAM } from '@/constants/Home';

const MIXPANEL_HQ_ENTRY_SOURCE_KEY = 'mixpanel_hq_entry_source';
const MIXPANEL_HQ_ENTRY_REFERRER_KEY = 'mixpanel_hq_entry_referrer';

/** Hostname patterns for apps that may link into HQ with a trusted fromUrl. */
const INBOUND_FROM_URL_HOST_PATTERNS = [
  /^localhost$/,
  /^127\.0\.0\.1$/,
  /^(www\.)?freshprints\.com$/,
  /^freshprints(-(dev|qa|stage|staging))?\.internal-fp\.com$/,
  /^(www\.)?corp(-(dev|qa|staging|stage))?(\.internal-fp|\.freshprints)\.com$/,
  /^fp\.freshprints\.com$/,
  /^dt(-(dev|qa|staging))?\.(freshprints|internal-fp)\.com$/,
  /^design-tool\.freshprints\.com$/,
  /^v(3|4)(-(dev|qa|staging))?\.(freshprints|internal-fp)\.com$/,
];

/** Mixpanel page names used as ?source= on inbound links (aligned with fp.com / freshcorp EEventSource). */
const VALID_INBOUND_SOURCE_VALUES = new Set<string>([
  'Homepage',
  'Product Gallery',
  'Design Gallery',
  'Design Page',
  'Product Page',
  'Top Customizations',
  'Similar Design',
  'More Designs',
  'Design Mockup Product Gallery',
  'Reviews',
  'Size Chart',
  'Print Guid',
  'Share',
  'About Us',
  'Brand Filter',
  'Favorite Page',
  'Chapter Page',
  'Theme Page',
  'Blog Page',
  'Blog Details Page',
  'School Page',
  'Bottom Nav',
  'Error Page',
  'How To Merch Page',
  'Not Found Page',
  'Login Page',
  'Signup Page',
  'Forgot Password Page',
  'BTS Page',
  'Legal',
  'Help Center',
  'FAQ',
  'Collection Gallery',
  'Collection Page',
  'Services Page',
  'FP.com Nav Bar',
  'Design Tool',
]);

export const isTrustedInboundFromUrl = (fromUrl: string | null | undefined): boolean => {
  if (!fromUrl?.trim()) return false;
  try {
    return INBOUND_FROM_URL_HOST_PATTERNS.some((pattern) =>
      pattern.test(new URL(fromUrl).hostname)
    );
  } catch {
    return false;
  }
};

export const isValidInboundSource = (source: string | null | undefined): source is string => {
  const trimmed = source?.trim();
  return Boolean(trimmed && VALID_INBOUND_SOURCE_VALUES.has(trimmed));
};

export const resolveInboundEntryFromSearchParams = (
  searchParams: URLSearchParams
): { source: string | null; referrer: string | null } => {
  const fromUrl = searchParams.get(NAVIGATION_FROM_URL_PARAM);
  const trustedReferrer =
    fromUrl?.trim() && isTrustedInboundFromUrl(fromUrl) ? fromUrl.trim() : null;
  const sourceParam = searchParams.get('source')?.trim() ?? null;
  const source =
    sourceParam && sourceParam !== 'HQ' && isValidInboundSource(sourceParam) ? sourceParam : null;

  return { source, referrer: trustedReferrer };
};

const storeInboundEntry = (source: string | null, referrer: string | null): void => {
  try {
    if (source) {
      sessionStorage.setItem(MIXPANEL_HQ_ENTRY_SOURCE_KEY, source);
    } else {
      sessionStorage.removeItem(MIXPANEL_HQ_ENTRY_SOURCE_KEY);
    }

    if (referrer) {
      sessionStorage.setItem(MIXPANEL_HQ_ENTRY_REFERRER_KEY, referrer);
    } else {
      sessionStorage.removeItem(MIXPANEL_HQ_ENTRY_REFERRER_KEY);
    }
  } catch {
    // sessionStorage may be unavailable
  }
};

/** Persist inbound source/referrer on landing; overwrites stale session values when URL carries new entry data. */
export const persistInboundEntryFromSearchParams = (
  searchParams: URLSearchParams
): { source: string | null; referrer: string | null } => {
  if (typeof window === 'undefined') return { source: null, referrer: null };

  const { source, referrer } = resolveInboundEntryFromSearchParams(searchParams);
  if (source || referrer) {
    storeInboundEntry(source, referrer);
    return { source, referrer };
  }

  try {
    return {
      source: sessionStorage.getItem(MIXPANEL_HQ_ENTRY_SOURCE_KEY),
      referrer: sessionStorage.getItem(MIXPANEL_HQ_ENTRY_REFERRER_KEY),
    };
  } catch {
    return { source: null, referrer: null };
  }
};

export const persistInboundEntryFromLocation = (): {
  source: string | null;
  referrer: string | null;
} => {
  if (typeof window === 'undefined') return { source: null, referrer: null };
  return persistInboundEntryFromSearchParams(new URLSearchParams(window.location.search));
};

export const resolveMixpanelInboundSource = (): string | null => {
  if (typeof window === 'undefined') return null;

  const fromLocation = resolveInboundEntryFromSearchParams(
    new URLSearchParams(window.location.search)
  );
  if (fromLocation.source) return fromLocation.source;

  try {
    const stored = sessionStorage.getItem(MIXPANEL_HQ_ENTRY_SOURCE_KEY);
    if (stored) return stored;
  } catch {
    // ignore
  }

  return null;
};

export const resolveMixpanelInboundReferrer = (): string | null => {
  if (typeof window === 'undefined') return null;

  const fromLocation = resolveInboundEntryFromSearchParams(
    new URLSearchParams(window.location.search)
  );
  if (fromLocation.referrer) return fromLocation.referrer;

  try {
    const stored = sessionStorage.getItem(MIXPANEL_HQ_ENTRY_REFERRER_KEY);
    if (stored) return stored;
  } catch {
    // ignore
  }

  const documentReferrer = typeof document !== 'undefined' ? document.referrer : '';
  return documentReferrer || null;
};

export const resolveMixpanelReferrerProperties = (): Record<string, string> => {
  const referrer = resolveMixpanelInboundReferrer();
  if (!referrer) return {};

  try {
    const referrerUrl = new URL(referrer);

    return {
      $referrer: referrer,
      $referring_domain: referrerUrl.hostname,
      $initial_referrer: referrer,
      $initial_referring_domain: referrerUrl.hostname,
    };
  } catch {
    return { $referrer: referrer, $initial_referrer: referrer };
  }
};
